import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock, ChevronLeft, ChevronRight, ListChecks, XCircle } from 'lucide-react';
import { cancelReminderSchedule, listReminderSchedules } from '../../../../api/collections';
import { showToast } from '../../../../utils/showToast';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { Button } from '../../../../components/ui/Button';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { Modal } from '../../../../components/ui/Modal';
import { TableSkeleton } from '../../../../components/ui/TableSkeleton';
import DispatchStatusBadge from '../components/DispatchStatusBadge';
import {
  CHANNEL_LABELS,
  DISPATCH_STATUSES,
  apiErrorStatus,
  collectionsErrorText,
  formatInstant,
  formatLocalDateTime,
} from '../utils/collectionReminders';

const PAGE_SIZE = 20;
// Fecha de envío · Canales · Alcance · Estado · Creado · Acciones
const GRID_COLS = '1.1fr 0.9fr 1fr 1.8fr 1fr auto';

// Filas del lote que "Cancelar" alcanza. Billing lo informa en `cancellable`: las programadas
// y las que esperan reintento sin haber podido salir (no las de resultado incierto). Un
// billing anterior no lo manda: entonces solo se cuentan las programadas.
const cancellableCount = (s) =>
  typeof s?.cancellable === 'number' ? s.cancellable : Number(s?.counts?.scheduled) || 0;

function StatusCounts({ counts }) {
  const present = DISPATCH_STATUSES.filter((s) => Number(counts?.[s]) > 0);
  if (present.length === 0) return <span className="bill-td-sub">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {present.map((s) => (
        <DispatchStatusBadge key={s} status={s} size="sm" count={counts[s]} />
      ))}
    </span>
  );
}

/**
 * Envíos programados del operador (uno por tanda: notas × canales), con el estado de sus
 * filas y la opción de cancelar lo que todavía no salió. Se crean desde la lista de notas
 * ("Programar recordatorio"); acá solo se listan y cancelan.
 *
 * @param {{ timeZone?: string, onShowDispatches: (batchId: string) => void }} props
 */
export default function ReminderSchedulesTab({ timeZone, onShowDispatches }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listReminderSchedules({ limit: PAGE_SIZE, offset });
      setPage({ items: data?.items ?? [], count: data?.count ?? 0 });
    } catch (err) {
      setError(collectionsErrorText(err, t, 'No se pudieron cargar los envíos programados.'));
    } finally {
      setLoading(false);
    }
  }, [offset, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await cancelReminderSchedule(cancelTarget.batchId);
      showToast(t('Se cancelaron {{n}} envíos.', { n: data?.cancelled ?? 0 }), 'success');
    } catch (err) {
      // 404: billing ya no lo encuentra para este operador; se refresca la lista igual para no
      // dejar a la vista una fila que no existe.
      showToast(
        apiErrorStatus(err) === 404
          ? t('Ese envío programado ya no existe.')
          : collectionsErrorText(err, t, 'No se pudo cancelar el envío programado.'),
        'error',
      );
    } finally {
      setCancelling(false);
      setCancelTarget(null);
      load();
    }
  };

  const totalPages = Math.max(1, Math.ceil(page.count / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const sendAt = (s) => formatLocalDateTime(s.sendAtLocal) || formatInstant(s.notBefore, timeZone) || '—';
  const channelsText = (s) => (s.channels ?? []).map((c) => t(CHANNEL_LABELS[c] ?? c)).join(' · ') || '—';
  const scopeText = (s) => t('{{n}} notas · {{c}} comercios', { n: s.notes ?? 0, c: s.companies ?? 0 });

  const actions = (s) => (
    <>
      <Button size="sm" variant="outline" iconLeft={<ListChecks className="h-4 w-4" />} onClick={() => onShowDispatches(s.batchId)}>
        {t('Ver envíos')}
      </Button>
      {cancellableCount(s) > 0 && (
        <Button size="sm" variant="ghost" iconLeft={<XCircle className="h-4 w-4" />} onClick={() => setCancelTarget(s)}>
          {t('Cancelar')}
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-3">
      {timeZone && (
        <p className="text-xs text-fg-muted">{t('Horas en {{zona}}, la zona del operador.', { zona: timeZone })}</p>
      )}

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={load}>
            {t('Reintentar')}
          </Button>
        </div>
      )}

      <div className="bill-card bill-card--clip">
        {loading ? (
          <TableSkeleton rows={5} columns={6} className="border-0" />
        ) : page.items.length === 0 ? (
          !error && (
            <EmptyState
              icon={<CalendarClock />}
              title={t('No hay envíos programados')}
              description={t('Para programar uno, seleccioná notas en la lista y elegí "Programar recordatorio".')}
              action={
                <Button variant="outline" onClick={() => navigate('/admin/cobro')}>
                  {t('Ir a las notas de cobro')}
                </Button>
              }
            />
          )
        ) : isMobile ? (
          <div className="bill-cards">
            {page.items.map((s) => (
              <div key={s.batchId} className="bill-card-item">
                <div className="bill-card-item__head">
                  <span className="bill-card-item__title">{sendAt(s)}</span>
                  <span className="bill-td-muted">{channelsText(s)}</span>
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Alcance')}</span>
                    <span className="bill-card-item__v">{scopeText(s)}</span>
                  </div>
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Estado')}</span>
                    <StatusCounts counts={s.counts} />
                  </div>
                </div>
                <div className="bill-card-item__foot">{actions(s)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': GRID_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Fecha de envío')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Canales')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Alcance')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Estado')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Creado')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Acciones')}</span>
            </div>
            {page.items.map((s) => (
              <div key={s.batchId} className="bill-grid__row" role="row">
                <span className="bill-td-strong" role="cell">{sendAt(s)}</span>
                <span className="bill-td-muted" role="cell">{channelsText(s)}</span>
                <span className="bill-td-muted" role="cell">{scopeText(s)}</span>
                <span role="cell">
                  <StatusCounts counts={s.counts} />
                </span>
                <span className="bill-td-sub" role="cell">{formatInstant(s.createdAt, timeZone) || '—'}</span>
                <span className="bill-grid__cell--actions" role="cell">
                  {actions(s)}
                </span>
              </div>
            ))}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label={t('Anterior')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>
              {t('Página')} {currentPage} {t('de')} {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label={t('Siguiente')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        size="sm"
        title={t('Cancelar envío programado')}
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>
              {t('Volver')}
            </Button>
            <Button variant="danger" onClick={handleCancel} loading={cancelling}>
              {t('Cancelar envío')}
            </Button>
          </>
        }
      >
        {cancelTarget && (
          <p className="m-0 text-sm text-fg">
            {t('Se cancelarán los {{n}} envíos del {{fecha}} que todavía no salieron. Lo ya enviado no se toca.', {
              n: cancellableCount(cancelTarget),
              fecha: sendAt(cancelTarget),
            })}
          </p>
        )}
      </Modal>
    </div>
  );
}
